import { useState } from "react";
import {
  FullCsticId,
  type LinearCsticValue,
  type LinearSubstep,
  SimpleCsticId,
} from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import { type RootState } from "~/app/store";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useTranslation } from "react-i18next";
import { Button, Grid, Stack, styled } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import { TabContext, TabPanel } from "@mui/lab";
import { Tab } from "~/common/mui/Tabs/Tab";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import { CARRIAGE_DETAILS_INFO_MSG } from "~/features/configurator/Carriage/carriageConfiguratorConstants";
import { RESET_STANDARD } from "~/features/configurator/common/advancedTranslationUtils";
import { LinearTranslation } from "~/features/configurator/common/components/LinearTranslation";
import { csticHasValue, getSubKmatCstics } from "~/features/configurator/common/v2/helper";
import { CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import type { CsticUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { updateCsticValues } from "~/features/configurator/Linear/redux/actions";
import {
  getProductCode,
  selectCarriagesPerGuidewayAmount,
  selectRailAmount,
  selectSubKmat,
} from "~/features/configurator/Linear/redux/selectors";
import { SimpleCsticSelectField } from "~/features/configurator/Shaft/components/SimpleCsticSelectField";
import {
  LinearSection,
  LinearSectionTitle,
} from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/GuidewaySetStep";
import { KitInfoWrapper } from "~/features/configurator/Linear/components/Substeps/CarriageDetailsSubstep/CarriageDetailsSubstep";
import { CsticNames } from "~/features/configurator/Linear/components/Substeps/CarriageDetailsSubstep/constants";
import { Star } from "@mui/icons-material";
import { SimpleCsticSelectPopup } from "~/features/configurator/Carriage/components/ConfigureCarriageSubstep/SimpleCsticSelectPopup";
import { LCTileImage } from "~/features/configurator/Linear/components/LinearTile";
import { GET_CARRIAGE_DETAILS_IMAGE_URL } from "~/features/configurator/Linear/constants";
import {
  CarriageLocatingFaceSegment1,
  CarriageLocatingFaceSegment2,
} from "~/features/configurator/Linear/v2/configuration/kllt/KLLTUnitCstics";
import { selectCsticExpanded } from "~/features/configurator/Linear/redux/selectCstic";
import { ExtraConfig } from "./constants";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const KLLTCarriageDetailsSubstep = ({ def, ...props }: Props) => {
  const [activeTabIndex, setActiveTab] = useState(0);
  const railAmount = useSelector(selectRailAmount);
  const carriageAmount = useSelector(selectCarriagesPerGuidewayAmount);

  const { t } = useTranslation();
  if (!railAmount) {
    return null;
  }

  const tabList = new Array(railAmount).fill(0);
  const carriageList = new Array(carriageAmount).fill(0);

  const getTabLabel = (guidewayIndex: number) =>
    t("page.linear.carriageDetails.GuidewayTabTitle", { index: guidewayIndex + 1 });

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      {def.footerInfo.length > 0 && (
        <div className="mb-3">
          {def.footerInfo.map((info) => (
            <Alert key={info} className="mb-2" severity="info">
              <LanguageAwareExternalContent content={info} className={"scha__lc__external-label"} />
            </Alert>
          ))}
        </div>
      )}

      <div className={"w-100 mb-30"}>
        <TabContext value={activeTabIndex}>
          <Tabs
            onChange={(e, val) => setActiveTab(val)}
            value={activeTabIndex}
            variant="fullWidth"
            fullWidthIndicator={false}
          >
            {tabList.map((_, railIndex) => (
              <Tab key={railIndex} value={railIndex} label={getTabLabel(railIndex)} />
            ))}
          </Tabs>
          {tabList.map((_, railIndex) => (
            <TabPanel value={railIndex} keepMounted sx={{ paddingInline: 0 }}>
              <Grid container gap={"var(--gutter)"} columns={3}>
                {carriageList.map((_, carriageIndex) => (
                  <Grid size={1} key={carriageIndex}>
                    <SingleTile
                      def={def}
                      kmatType={KmatType.SubCarriage}
                      railIndex={railIndex}
                      railAmount={railAmount}
                      carriageIndex={carriageIndex}
                      carriageAmount={carriageAmount}
                      disabled={true}
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
          ))}
        </TabContext>
      </div>
    </SubstepFrame>
  );
};
export default KLLTCarriageDetailsSubstep;

interface TileProps {
  def: LinearSubstep;
  kmatType: KmatType;
  railIndex: number;
  railAmount: number;
  carriageIndex: number;
  carriageAmount: number;
  disabled?: boolean;
}

const SingleTile = ({ def, kmatType, railIndex, railAmount, carriageIndex, carriageAmount }: TileProps) => {
  const dispatch = useDispatch();
  const flatCarriageIndex = railIndex * carriageAmount + carriageIndex;
  const kmat = useSelector(selectSubKmat(KmatType.SubCarriage, flatCarriageIndex));
  const cstics = kmat.cstics;

  const { input: inputCstics, helper: helperCstics } = getSubKmatCstics(def, flatCarriageIndex);
  const makeCstiId = (name: string) => FullCsticId({ key: name, index: flatCarriageIndex, type: def.kmatType });

  const leftCsticHelper = inputCstics[CsticNames.KITLeft];
  const leftCstic = cstics[leftCsticHelper.name];

  const middleCsticHelper = inputCstics[CsticNames.KITCenter];
  const middleCstic = cstics[middleCsticHelper.name];

  const rightCsticHelper = inputCstics[CsticNames.KITRight];
  const rightCstic = cstics[rightCsticHelper.name];

  const lubricationOrientationHelper = inputCstics[CsticNames.LubricationConnectorOrientation];
  const lubricationOrientationCstic = cstics[lubricationOrientationHelper.name];

  const lubricationPositionHelper = inputCstics[CsticNames.LubricationConnectorPosition];
  const lubricationConnectorPositionCstic = cstics[lubricationPositionHelper.name];

  const lubricationConnectorHelper = inputCstics[CsticNames.LubricationConnector];

  const lubricationConnectorAlignmentHelper = inputCstics[CsticNames.LubricationConnectorAlignment];
  const lubricationConnectorAlignmentCstic = cstics[lubricationConnectorAlignmentHelper.name];

  const upperDisclaimer = useSelector(selectUpperDisclaimer);

  const standardKitsHelper = helperCstics[CsticNames.StandardKits];

  const productCode = useSelector(getProductCode);
  const isClassicCarriage = productCode === CARRIAGE_CONFIGURATOR_PRODUCT_CODE;

  const isEditButtonEnabled = csticHasValue(kmat, helperCstics[CsticNames.EnableCarriageEditButton].name);

  const editDetails = () =>
    dispatch(
      updateCsticValues(
        [
          {
            type: KmatType.SubCarriage,
            index: flatCarriageIndex,
            value: isClassicCarriage ? null : "N",
            key: standardKitsHelper.name,
          },
        ] as CsticUpdate[],
        SubstepType.KLLTCarriageDetails,
      ),
    );
  const resetDetails = () =>
    dispatch(
      updateCsticValues(
        [
          {
            type: KmatType.SubCarriage,
            index: flatCarriageIndex,
            value: "S",
            key: standardKitsHelper.name,
          },
        ] as CsticUpdate[],
        SubstepType.KLLTCarriageDetails,
      ),
    );

  // TODO - Move to configuration
  let locatingFaceCsticName;
  if (railIndex === 0) {
    locatingFaceCsticName = CarriageLocatingFaceSegment1;
  } else {
    locatingFaceCsticName = CarriageLocatingFaceSegment2;
  }
  const { cstic: locatingFaceCstic } = useSelector(
    selectCsticExpanded(SimpleCsticId(locatingFaceCsticName), SubstepType.LocatingFace),
  );

  const { t } = useTranslation();
  const title = t(`page.linear.carriageDetails.title.${locatingFaceCstic?.value == "U" ? "below" : "above"}`, {
    carriageIndex: carriageIndex + 1,
    guidewayIndex: railIndex + 1,
  });

  const carriageImageUrl = GET_CARRIAGE_DETAILS_IMAGE_URL({
    carriageIndex: flatCarriageIndex,
    carriageAmount,
    guidewayAmount: railAmount,
  });

  const carriageDesignEditable = def.extraConfig[ExtraConfig.carriageDesignEditable] as boolean;
  const singleCarriageMode = def.extraConfig[ExtraConfig.singleCarriageMode] as boolean;
  const showActionButtons = !singleCarriageMode || carriageIndex === 0;

  return (
    <Card disableHover={true} className={"pt-3 h-100"}>
      <div className={"px-gutter-half"}>
        {kmatType === KmatType.SubCarriage && (
          <>
            <LCTileImage className={"w-100"} imageUrl={carriageImageUrl} />
            <LinearSectionTitle className={"mt-0 mb-1"}>{title}</LinearSectionTitle>

            <SimpleCsticSelectPopup
              className={"mb-30"}
              cstic={inputCstics[CsticNames.CarriageDesign]}
              substepType={SubstepType.KLLTCarriageDetails}
              showEditButton={carriageDesignEditable}
            />
          </>
        )}
        {upperDisclaimer && (
          <Alert className="mb-30" severity="info">
            <LanguageAwareExternalContent content={upperDisclaimer} className={"scha__lc__external-label"} />
          </Alert>
        )}

        <LinearSectionTitle className={"mt-0 mb-3 f-bold"} info={def.extraTexts["sealingAndLubricationSectionInfo"]}>
          <LanguageAwareExternalContent
            content={def.extraTexts["sealingAndLubricationSectionName"]}
            className={"scha__lc__external-label"}
          />
        </LinearSectionTitle>

        <LinearSection label={""}>
          <Grid columns={3} gap={"var(--gutter)"} container sx={{ width: "100%" }}>
            <Grid size={1}>
              <KitInfoWrapper cstic={leftCstic} addModalParam={true}>
                <SimpleCsticSelectField
                  useCsticLabel
                  csticId={makeCstiId(leftCsticHelper.name)}
                  disabled={leftCsticHelper.disabled || !showActionButtons}
                  required={leftCsticHelper.required}
                  substepType={SubstepType.KLLTCarriageDetails}
                  renderValue={renderDropdownEntryWithStar}
                  shrink={true}
                  showFilter={true}
                />
              </KitInfoWrapper>
            </Grid>

            <Grid size={1}>
              <KitInfoWrapper cstic={middleCstic} addModalParam={true} hidden={!middleCsticHelper.visible}>
                <SimpleCsticSelectField
                  useCsticLabel
                  csticId={makeCstiId(middleCsticHelper.name)}
                  disabled={middleCsticHelper.disabled || !showActionButtons}
                  required={middleCsticHelper.required}
                  substepType={SubstepType.KLLTCarriageDetails}
                  renderValue={renderDropdownEntryWithStar}
                  shrink={true}
                  showFilter={true}
                />
              </KitInfoWrapper>
            </Grid>

            <Grid size={1}>
              <KitInfoWrapper cstic={rightCstic} addModalParam={true} hidden={!rightCsticHelper.visible}>
                <SimpleCsticSelectField
                  useCsticLabel
                  csticId={makeCstiId(rightCsticHelper.name)}
                  disabled={rightCsticHelper.disabled || !showActionButtons}
                  required={rightCsticHelper.required}
                  substepType={SubstepType.KLLTCarriageDetails}
                  renderValue={renderDropdownEntryWithStar}
                  shrink={true}
                  showFilter={true}
                />
              </KitInfoWrapper>
            </Grid>
          </Grid>
        </LinearSection>

        {lubricationOrientationHelper.visible && (
          <LinearSection
            info={lubricationOrientationCstic.info}
            label={
              <LanguageAwareExternalContent
                content={lubricationOrientationCstic.label}
                className={"scha__lc__external-label"}
              />
            }
          >
            <SimpleCsticSelectField
              csticId={makeCstiId(lubricationOrientationHelper.name)}
              disabled={lubricationOrientationHelper.disabled || !showActionButtons}
              required={lubricationOrientationHelper.required}
              substepType={SubstepType.KLLTCarriageDetails}
              renderValue={renderDropdownEntryWithStar}
            />
          </LinearSection>
        )}

        {lubricationPositionHelper.visible && (
          <LinearSection
            info={lubricationConnectorPositionCstic.info}
            label={
              <LanguageAwareExternalContent
                content={lubricationConnectorPositionCstic.label}
                className={"scha__lc__external-label"}
              />
            }
          >
            <SimpleCsticSelectField
              csticId={makeCstiId(lubricationPositionHelper.name)}
              disabled={lubricationPositionHelper.disabled || !showActionButtons}
              required={lubricationPositionHelper.required}
              substepType={SubstepType.KLLTCarriageDetails}
              renderValue={renderDropdownEntry}
            />
          </LinearSection>
        )}

        {lubricationConnectorHelper.visible && (
          <SimpleCsticSelectPopup
            className={"pb-30"}
            cstic={inputCstics[CsticNames.LubricationConnector]}
            tabsCstic={helperCstics[CsticNames.LubricationConnectorType]}
            substepType={SubstepType.KLLTCarriageDetails}
            showEditButton={showActionButtons}
          />
        )}

        {lubricationConnectorAlignmentHelper.visible && (
          <LinearSection
            info={lubricationConnectorAlignmentCstic.info}
            label={
              <LanguageAwareExternalContent
                content={lubricationConnectorAlignmentCstic.label}
                className={"scha__lc__external-label"}
              />
            }
          >
            <SimpleCsticSelectField
              csticId={makeCstiId(lubricationConnectorAlignmentHelper.name)}
              disabled={lubricationConnectorAlignmentHelper.disabled || !showActionButtons}
              required={lubricationConnectorAlignmentHelper.required}
              substepType={SubstepType.KLLTCarriageDetails}
              renderValue={renderDropdownEntry}
            />
          </LinearSection>
        )}

        {showActionButtons && (
          <Stack direction={"row"} gap={2} sx={{ pb: 2 }}>
            <Button color={"secondary"} fullWidth={true} onClick={resetDetails} disabled={!isEditButtonEnabled}>
              <LinearTranslation translationKey={RESET_STANDARD} />
            </Button>
            <Button color={"primary"} fullWidth={true} onClick={editDetails} disabled={!isEditButtonEnabled}>
              <LanguageAwareExternalContent content={def.extraTexts["edit"]} className={"scha__lc__external-label"} />
            </Button>
          </Stack>
        )}
      </div>
    </Card>
  );
};

const renderDropdownEntryWithStar = (value: LinearCsticValue) => (
  <Stack direction="row" gap={0.5} alignItems={"center"}>
    <Star fontSize="small" visibility={value.standard ? undefined : "hidden"} />
    <TruncableDropdownContent content={value.label} />
  </Stack>
);

const TruncableDropdownContent = styled(LanguageAwareExternalContent)({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const renderDropdownEntry = (value: LinearCsticValue) => <LanguageAwareExternalContent content={value.label} />;

const selectUpperDisclaimer = (state: RootState) =>
  state.linearConfigurator.specialTranslations[CARRIAGE_DETAILS_INFO_MSG];
